import React, { useEffect, useState } from 'react';
import 'react-calendar/dist/Calendar.css';
import moment from 'moment';
import { useTheme } from '../../../../theme-context';
import {
    DEPARTMENTS,
    FILES_TYPE,
    MISMATCH_REASONS,
    STATUS_RAG,
    TRANSACTIONS_TYPE
} from '../../../../utils/constants/insights-filter';
import { useAppDispatch, useAppSelector } from '../../../../app/hooks';
import { TRANSACTIONS_COLUMNS } from '../../../../utils/constants/transactions-columns';
import { IPaginationPayload } from '../../../../interfaces/shared/IPaginationPayload';
import {
    tryToExportTransactionReport,
    tryToFetchTransactionFilters,
    tryToFetchTransactions,
    tryToFetchTransactionsRuns
} from '../../../../store/brokers/partners/transactions/transactionsSlice';
import TabFilters from '../components/TabFilters';
import DetailsBox from '../components/DetailsBox';
import RangeSlider from '../components/RangeSlider';
import DateRangePopover from '../components/DateRange';
import TransactionsTable from '../tables/TransactionsTable';
import VoidedTable from '../tables/VoidedTable';
import ReviewTransactionsModal from '../modals/ReviewTransactionsModal';
import TransactionsDetailsModal from '../modals/TransactionsDetailsModal';
import ColumnPickerModal from '../modals/ColumnPickerModal';
import ExportModal from '../modals/ExportModal';
import SelectCheckbox from '../../../../shared/select-checkbox';
import Button from '../../../../shared/button';
import Input from '../../../../shared/input';
import UnmatchedTransactionsModal from '../modals/UnmatchedTransactionsModal';
import { tryToFetchExecutiveStats } from '../../../../store/brokers/partners/admin/adminSlice';
import BankReconModal from '../../../../components/bank-recon/BankReconModal';
import ManualTransactionModal from '../modals/ManualTransactionModal';


const DetailedTab = ({
	status
}: {
	status?: string
}) => {
    const { theme } = useTheme();
    const dispatch = useAppDispatch();
    const [rows, setRows] = useState<any[]>([]);
    const [runsData, setRunsData] = useState<any>();
    const [paginationState, setPaginationState] = useState<IPaginationPayload>({
        pageNumber: 1,
        pageSize: 10,
    });
    const [selectedLatestRun, setSelectedLatestRun] = useState<any>();
    const [selectedDateRange, setSelectedDateRange] = useState<any>();
    const [selectedFileType, setSelectedFileType] = useState<any>();
    const [selectedRag, setSelectedRag] = useState<any>();
    const [selectedMismatch, setSelectedMismatch] = useState<any>();
    const [selectedTransactions, setSelectedTransactions] = useState<any>();
    const [selectedDepartment, setSelectedDepartment] = useState<any>();
    const [posDateRange, setPosDateRange] = useState<[Date, Date] | null>(null);
    const [pspDateRange, setPspDateRange] = useState<[Date, Date] | null>(null);
    const [varianceAmountData, setVarianceAmountData] = useState<{ min: number, max: number }>();
    const [varianceAmount, setVarianceAmount] = useState<number>();
    const [transactionAmountData, setTransactionAmountData] = useState<{ min: number, max: number }>();
    const [transactionAmount, setTransactionAmount] = useState<number>();
    const [searchText, setSearchText] = useState<string>();
    const [filesTypeData, setFilesTypeData] = useState<any>(FILES_TYPE);
    const [departmentsData, setDepartmentsData] = useState<any>(DEPARTMENTS);
    const [reasonsData, setReasonsData] = useState<any>(MISMATCH_REASONS);
    const [typesData, setTypesData] = useState<any>([TRANSACTIONS_TYPE?.[0]]);
    const [allColumns, setAllColumns] = useState<any>();
    const [showColumnsModal, setShowColumnsModal] = useState<boolean>(false);
    const [showExportModal, setShowExportModal] = useState<boolean>(false);
    const [sortSelected, setSortSelected] = useState<any>();
    const [specificDate, setSpecificDate] = useState<any>();
    const [showBankModal, setShowBankModal] = useState<boolean>(false);
    const [transactionsView, setTransactionsView] = useState<string>('main-view')
    const [showDetailsModal, setShowDetailsModal] = useState<{ show: boolean, row?: any }>();
    const [showTransactionModal, setShowTransactionModal] = useState<{ show: boolean, row?: any }>();
	const [showManualTransactionModal, setShowManualTransactionModal] = useState<boolean>();
    const [unmatchedModal, setUnmatchedModal] = useState<any>();
    const [selectedStatuses, setSelectedStatuses] = useState({
        'Reconciled': true,
        'Potential Match': true,
        'Unreconciled': true,
        'Pending': false,
    });
    const [loadingExportReport, setLoadingExportReport] = useState<any>();
    const transactionsData = useAppSelector(state => state.transactions.transactions?.data)
    const paginationData = useAppSelector((state) => state.transactions?.transactions?.data?.page);
    const runs = useAppSelector((state) => state?.transactions?.runs)
    const stats = useAppSelector((state) => state?.admin?.executiveStats)

    useEffect(() => {
        if (runs) {
            const allRows = [...runsData || [], ...runs?.data?.elements || []]?.filter((item, index, self) =>
                index === self.findIndex((t) => t._id === item._id)
            );
            const formatRows = allRows && allRows?.length > 0 && allRows?.map((item: any, idx: number) => ({ ...item || {}, label: `Run #${runs?.data?.page?.totalElements - idx} - ${moment(item?.createdAt)?.format('DD/MM/YYYY')}`, value: item?.createdAt }))
            setRunsData(formatRows || [])
        }
    }, [runs])

    const getRunsData = async (showMore?: boolean, search?: string) => {
        try {
            const data: any = {
                pageNumber: showMore ? (runs?.data?.page?.number || 1) + 1 : 1,
                pageSize: 10,
                filters: search || undefined,
            }
            await dispatch(tryToFetchTransactionsRuns(data)).unwrap();
        } catch (err) {
            console.error(err, 'error')
        }
    }

    const formatText = (text: string | null | undefined): string => {
        if (!text) return '';
        return text
            .replace(/_/g, ' ')
            .toLowerCase()
            .split(' ')
            .map(word =>
                word.charAt(0).toUpperCase() + word.slice(1)
            )
            .join(' ');
    };

    const getDateRange = (input: any) => {
        if (!input) return null;

        const formatDate = (dateStr: string, isStart: boolean): string => {
            const date = new Date(dateStr);
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            const time = isStart ? '00:00:00.000' : '23:59:59.999';
            return `${year}-${month}-${day}T${time}`;
        };

        if (Array.isArray(input) && input.length === 2) {
            const [start, end] = input;
            return {
                dateRangeMin: formatDate(start, true),
                dateRangeMax: formatDate(end, false),
            };
        }

        return {
            dateRangeMin: formatDate(input, true),
            dateRangeMax: formatDate(input, false),
        };
    };

    const onGettingStatsData = async (refresh?: boolean) => {
        try {
            const settingsFilters: any = {
                detailedView: true
            }
            await dispatch(tryToFetchExecutiveStats(settingsFilters)).unwrap()
        } catch (err) {
            console.error(err)
        }
    }

    const onGettingDynamicFilters = async (refresh?: boolean) => {
        try {
            let settingsFilters: any
            if (!refresh) {
                if (selectedLatestRun && selectedLatestRun?.value !== 'all') {
                    settingsFilters = {
                        ...settingsFilters || {},
                        run: selectedLatestRun?.value
                    }
                }
                if (selectedDateRange && selectedDateRange?.value !== 'all') {
                    const range = getDateRange([selectedDateRange?.[0], selectedDateRange?.[1]]);
                    settingsFilters = {
                        ...settingsFilters || {},
                        dateRangeMin: range?.dateRangeMin,
                        dateRangeMax: range?.dateRangeMax
                    }
                }
                if (specificDate) {
                    const specificDateRange = getDateRange(specificDate);
                    settingsFilters = {
                        ...settingsFilters || {},
                        dateRangeMin: specificDateRange?.dateRangeMin,
                        dateRangeMax: specificDateRange?.dateRangeMax
                    }
                }
            }
            const response = await dispatch(tryToFetchTransactionFilters(settingsFilters)).unwrap()
            setVarianceAmountData({
                min: response?.varianceRange?.min,
                max: response?.varianceRange?.max
            })
            setVarianceAmount(response?.varianceRange?.max)
            setTransactionAmountData({
                min: response?.transactionAmountRange?.min,
                max: response?.transactionAmountRange?.max
            })
            setTransactionAmount(response?.transactionAmountRange?.max)
            const formatFilesData = response?.fileTypes && response?.fileTypes?.length > 0 && response?.fileTypes?.map((item: any) => {
                return {
                    value: item,
                    label: formatText(item)
                }
            })
            setFilesTypeData([
                FILES_TYPE?.[0],
                ...formatFilesData || []
            ])
            const formatDepartments = response?.departments && response?.departments?.length > 0 && response?.departments?.map((item: any) => {
                return {
                    value: item,
                    label: formatText(item)
                }
            })
            setDepartmentsData([
                DEPARTMENTS?.[0],
                ...formatDepartments || []
            ])

            const formatReasons = response?.mismatchReasons && response?.mismatchReasons?.length > 0 && response?.mismatchReasons?.map((item: any) => {
                return {
                    value: item,
                    label: formatText(item)
                }
            })
            setReasonsData([
                MISMATCH_REASONS?.[0],
                ...formatReasons || []
            ])
            const formatTypes = response?.types && response?.types?.length > 0 && response?.types?.map((item: any) => {
                return {
                    value: item,
                    label: TRANSACTIONS_TYPE?.find((type: any) => (type?.value).toLowerCase() === (item || '')?.toLowerCase())?.label || formatText(item)
                }
            })
            setTypesData([
                TRANSACTIONS_TYPE?.[0],
                ...formatTypes || []
            ])
        } catch (err) {
            console.error(err)
        }
    }

    useEffect(() => {
        const storedColumns = localStorage.getItem('transactions_columns');
        if (storedColumns) {
            try {
                const parsedColumns = JSON.parse(storedColumns);
                setAllColumns(parsedColumns);
            } catch (error) {
                console.error('Failed to parse saved columns:', error);
                setAllColumns(TRANSACTIONS_COLUMNS)
            }
        } else {
            setAllColumns(TRANSACTIONS_COLUMNS)
        }
        getRunsData()
        onGettingDynamicFilters()
        onGettingStatsData()
    }, [])

    const handleSearch = (query: string) => {
        getRunsData(false, query)
    };

    useEffect(() => {
        if (transactionsData) {
            setRows(transactionsData?.elements || []);
        }
    }, [transactionsData]);

    const onResetPaginationState = () => {
        setPaginationState({
            pageNumber: 1,
            pageSize: paginationState?.pageSize || 10,
        })
    }


    const onGettingFilters = (download?: boolean) => {

        let settingsFilters: any
        if (!download) {
            settingsFilters = {
                ...paginationState
            }
        }
        if (searchText) {
            settingsFilters = {
                ...settingsFilters || {},
                data: {
                    search: searchText
                }
            }
        }
        if (selectedRag && selectedRag?.value !== 'all' || status) {
            settingsFilters = {
                ...settingsFilters || {},
                data: {
                    ...settingsFilters?.data || {},
                    ragStatus: selectedRag?.value || status,
                    ...(status === 'voided' && { view: 'voided' })
                }
            }
        }
        if (selectedDepartment && selectedDepartment?.value !== 'all') {
            settingsFilters = {
                ...settingsFilters || {},
                data: {
                    ...settingsFilters?.data || {},
                    departments: selectedDepartment?.value
                }
            }
        }

        if (selectedMismatch && selectedMismatch?.value !== 'all') {
            settingsFilters = {
                ...settingsFilters || {},
                data: {
                    ...settingsFilters?.data || {},
                    misMatch: selectedMismatch?.value
                }
            }
        }
        if (transactionsView === 'voided') {
            settingsFilters = {
                ...settingsFilters || {},
                data: {
                    ...settingsFilters?.data || {},
                    view: transactionsView
                }
            }
        }
        if (selectedLatestRun && selectedLatestRun?.value !== 'all') {
            settingsFilters = {
                ...settingsFilters || {},
                data: {
                    ...settingsFilters?.data || {},
                    run: selectedLatestRun?.value
                }
            }
        }
        if (sortSelected && (sortSelected?.psp)) {
            settingsFilters = {
                ...settingsFilters || {},
                data: {
                    ...settingsFilters?.data || {},
                    pspDateSort: sortSelected?.psp === 'new' ? -1 : 1
                }
            }
        }
		if (sortSelected && (sortSelected?.reason)) {
            settingsFilters = {
                ...settingsFilters || {},
                data: {
                    ...settingsFilters?.data || {},
                    reasonSort: sortSelected?.reason === 'new' ? -1 : 1
                }
            }
        }
		if (sortSelected && (sortSelected?.reference)) {
            settingsFilters = {
                ...settingsFilters || {},
                data: {
                    ...settingsFilters?.data || {},
                    matchingReferenceSort: sortSelected?.reference === 'new' ? -1 : 1
                }
            }
        }
        if (sortSelected && (sortSelected?.pos)) {
            settingsFilters = {
                ...settingsFilters || {},
                data: {
                    ...settingsFilters?.data || {},
                    posDateSort: sortSelected?.pos === 'new' ? -1 : 1
                }
            }
        }
        if (sortSelected && (sortSelected?.pspValue)) {
            settingsFilters = {
                ...settingsFilters || {},
                data: {
                    ...settingsFilters?.data || {},
                    pspValue: sortSelected?.pspValue === 'big' ? -1 : 1
                }
            }
        }
        if (sortSelected && (sortSelected?.posValue)) {
            settingsFilters = {
                ...settingsFilters || {},
                data: {
                    ...settingsFilters?.data || {},
                    posValue: sortSelected?.posValue === 'big' ? -1 : 1
                }
            }
        }
        if (sortSelected && (sortSelected?.variance)) {
            settingsFilters = {
                ...settingsFilters || {},
                data: {
                    ...settingsFilters?.data || {},
                    varianceSort: sortSelected?.variance === 'big' ? -1 : 1
                }
            }
        }
        if (selectedTransactions && selectedTransactions?.value !== 'all') {
            settingsFilters = {
                ...settingsFilters || {},
                data: {
                    ...settingsFilters?.data || {},
                    transactionType: selectedTransactions?.value
                }
            }
        }
        if (selectedFileType && selectedFileType?.value !== 'all') {
            settingsFilters = {
                ...settingsFilters || {},
                data: {
                    ...settingsFilters?.data || {},
                    fileType: selectedFileType?.value
                }
            }
        }
        if (selectedDateRange && selectedDateRange?.value !== 'all') {
            const range = getDateRange([selectedDateRange?.[0], selectedDateRange?.[1]]);
            settingsFilters = {
                ...settingsFilters || {},
                data: {
                    ...settingsFilters?.data || {},
                    dateRangeMin: range?.dateRangeMin,
                    dateRangeMax: range?.dateRangeMax
                }
            }
        }
        if (specificDate) {
            const specificDateRange = getDateRange(specificDate);
            settingsFilters = {
                ...settingsFilters || {},
                data: {
                    ...settingsFilters?.data || {},
                    dateRangeMin: specificDateRange?.dateRangeMin,
                    dateRangeMax: specificDateRange?.dateRangeMax
                }
            }
        }
        if (varianceAmount && varianceAmount !== varianceAmountData?.max) {
            settingsFilters = {
                ...settingsFilters || {},
                data: {
                    ...settingsFilters?.data || {},
                    varianceMin: varianceAmountData?.min,
                    varianceMax: varianceAmount
                }
            }
        }
        if (transactionAmount && transactionAmount !== transactionAmountData?.max) {
            settingsFilters = {
                ...settingsFilters || {},
                data: {
                    ...settingsFilters?.data || {},
                    transactionAmountMin: transactionAmountData?.min,
                    transactionAmountMax: transactionAmount
                }
            }
        }
        if (posDateRange) {
            settingsFilters = {
                ...settingsFilters || {},
                data: {
                    ...settingsFilters?.data || {},
                    dateFromPOS: moment(posDateRange?.[0])?.toString(),
                    dateToPOS: moment(posDateRange?.[1])?.toString()
                }
            }
        }
        if (pspDateRange) {
            settingsFilters = {
                ...settingsFilters || {},
                data: {
                    ...settingsFilters?.data || {},
                    dateFromPSP: moment(pspDateRange?.[0])?.toString(),
                    dateToPSP: moment(pspDateRange?.[1])?.toString()
                }
            }
        }
        return settingsFilters
    }

    const handleStatusToggle = (statusKey: string) => {
        setSelectedStatuses((prev: any) => ({
            ...prev,
            [statusKey]: !prev?.[statusKey]
        }));
    };

    const handleSortChange = (sortOrder: string, type: string) => {
        const newType = sortSelected?.[type] === sortOrder ? undefined : sortOrder
        setSortSelected((prev: any) => ({
            ...prev || {},
            [type]: newType
        }))
        onResetPaginationState();
    };

    const onGettingData = async () => {
        try {
            const settingsFilters = onGettingFilters()
            await dispatch(tryToFetchTransactions(settingsFilters)).unwrap()
        } catch (err) {
            console.error(err)
        }
    }

    useEffect(() => {
        onGettingData()
    }, [paginationState]);

    const resetFilters = () => {
        setPosDateRange(null);
        setPspDateRange(null);
        setVarianceAmount(varianceAmountData?.max);
        setTransactionAmount(transactionAmountData?.max);
        setSearchText(undefined);
        setSelectedDepartment(undefined)
        setSelectedFileType(undefined)
        setSelectedMismatch(undefined)
        setSelectedTransactions(undefined)
        setSelectedRag(undefined)
        onResetPaginationState();
    };

    const onChangeFilters = (value: any, type: string) => {
        if (type === 'latestRun') {
            setSelectedLatestRun(value)
            setSpecificDate(undefined)
        }
        else {
            setSpecificDate(value)
            setSelectedLatestRun(undefined)
        }
        setSelectedDateRange(undefined)
        onResetPaginationState();
    };

    const onChangeSearchValue = (e: any) => {
        setSearchText(e)
        onResetPaginationState();
    }

    const onChangeSelectedFileType = (value: any) => {
        setSelectedFileType(value)
        onResetPaginationState();
    }

    const onClickColumnsModal = () => {
        setShowColumnsModal(!showColumnsModal)
    }

    const onClickExportModal = () => {
        setShowExportModal(!showExportModal)
    }

    const onClickShowTransactionModal = (transaction: any) => {
        setShowTransactionModal({
            show: true,
            row: transaction
        })
    }

    const onChangeActiveColumns = (column: any, hide: boolean) => {
        const updatedColumns = allColumns?.map((item: any) =>
            item?.key === column?.key ? { ...item, hide } : item
        );
        setAllColumns(updatedColumns);

        localStorage.setItem('transactions_columns', JSON.stringify(updatedColumns));
    };

    const onChangeSelectedFilter = (value: any, type?: string) => {
        if (type) {
            switch (type) {
                case 'rag':
                    setSelectedRag(value);
                    break;
                case 'mismatch':
                    setSelectedMismatch(value)
                    break;
                case 'transactions':
                    setSelectedTransactions(value)
                    break;
                case 'department':
                    setSelectedDepartment(value)
                    break;
            }
            onResetPaginationState();
        }
    }

	useEffect(()=>{
		if (status) {
			switch (status) {
				case 'green':
					setSelectedRag({label: 'Reconciled (Green)', value: 'green'})
					break;
				case 'amber':
					setSelectedRag({label: 'Potential Match (Amber)', value: 'amber'})
					break;
				case 'red':
					setSelectedRag({label: 'Unreconciled (Red)', value: 'red'})
					break;
			}
		}
	}, [status])

    const onChangeTransactionDatePOS = (value: any) => {
        setPosDateRange(value)
        onResetPaginationState()
    }

    const onChangeTransactionDatePSP = (value: any) => {
        setPspDateRange(value)
        onResetPaginationState()
    }

    const onChangeVarianceAmount = (value: any) => {
        setVarianceAmount(value)
        onResetPaginationState();
    }

    const onChangeTransactionsAmount = (value: any) => {
        setTransactionAmount(value)
        onResetPaginationState();
    }

    const onClickRefresh = () => {
        setSelectedDateRange(undefined)
        setSelectedLatestRun(undefined)
        setSpecificDate(undefined)
        onGettingDynamicFilters(true)
        onResetPaginationState();
    }

    const onChangeDateRange = (value: any) => {
        setSelectedDateRange(value)
        setSelectedLatestRun(undefined)
        setSpecificDate(undefined)
    }

    const onClickUpdateView = () => {
        onGettingDynamicFilters()
        onResetPaginationState()
    }

    const onChangeTransactionsView = (tab: string) => {
        if (tab !== transactionsView) {
            setTransactionsView(tab)
            if (tab === 'voided') {
                setSelectedRag(undefined)
            }
            onResetPaginationState()
        } else {
            setTransactionsView(tab)
        }
    }

    const onOpenTransactionDetails = (row: any) => {
        setShowTransactionModal(undefined)
        setShowDetailsModal({
            show: true,
            row: row
        })
    }

    const onReviewSuccessfully = async (reviewId?: string, newStatus?: string) => {
        if (selectedRag) {
            const formatRows: any = (rows?.length > 0) && rows?.filter((item: any) => item?.transactionId !== reviewId)
            setRows(formatRows)
        } else if (newStatus) {
            const formatRows: any = rows?.length > 0 && rows?.map((item: any) => {
                if (reviewId === item?.transactionId) {
                    return {
                        ...item || {},
                        status: newStatus
                    }
                }
                return item
            })
            setRows(formatRows)
        }
        await onGettingData()
    }

    const onClickExportReport = async () => {
        try {
            setLoadingExportReport(true)
            let settingsFilters: any
            if (selectedLatestRun && selectedLatestRun?.value !== 'all') {
                settingsFilters = {
                    ...settingsFilters || {},
                    run: selectedLatestRun?.value
                }
            }
            if (selectedDateRange && selectedDateRange?.value !== 'all') {
                settingsFilters = {
                    ...settingsFilters || {},
                    dateRangeMin: moment(selectedDateRange?.[0])?.toISOString(),
                    dateRangeMax: moment(selectedDateRange?.[1])?.toISOString()
                }
            }
            const response = await dispatch(tryToExportTransactionReport(settingsFilters)).unwrap()
            window.open(response?.url, '_blank')
        } catch (err) {
            console.error(err)
        } finally {
            setLoadingExportReport(false)
        }
    }

    useEffect(() => {
        onGettingDynamicFilters()
    }, [selectedDateRange, selectedLatestRun])

    const onClickUnmatchedItems = () => {
        setUnmatchedModal(!unmatchedModal)
    }

    const onClickMonthlyRecon = () => {
        setShowBankModal(!showBankModal)
    }

	const handleShowManualTransactionModal = () => {
		setShowManualTransactionModal(!showManualTransactionModal)
	}

    const itemsNumber = paginationData?.totalElements ? (paginationData?.totalElements < paginationData?.size) ? paginationData?.totalElements : (paginationData?.size * (paginationData?.number - 1) + (rows?.length || 0)) : 0

    return (
        <div>
			<ManualTransactionModal
				openModal={!!showManualTransactionModal}
				handleCloseModal={handleShowManualTransactionModal}
			/>
            <TabFilters
                daysProcessed={stats?.reportSummary?.daysProcessed}
                from={stats?.reportSummary?.from}
                to={stats?.reportSummary?.to}
                selectedDateRange={selectedDateRange}
                selectedLatestRun={selectedLatestRun}
                specificDate={specificDate}
                onChangeFilters={onChangeFilters}
                onChangeDateRange={onChangeDateRange}
                onClickRefresh={onClickRefresh}
                onClickUpdate={onClickUpdateView}
                handleSearchRuns={handleSearch}
                onLoadMore={() => getRunsData(true)}
                allRuns={runsData || []}
                hasNextPageRuns={runs?.data?.page?.hasNextPage}
            />

            <div className='my-4'>
                <p className='dark:text-primary-dark'>
                    <span className='font-bold text-xl'>Drill to Detail</span> - Transaction analysis
                </p>
                <DetailsBox
                    totalTransations={transactionsData?.totalTransactions}
                    mothlyBankRecon={transactionsData?.totalMonthlyTransactions}
                    matchRate={transactionsData?.matchRate}
                    monthlyDate={selectedDateRange?.[1] || specificDate || new Date}
                    unmatchedTransactions={transactionsData?.unmatchedTransactions}
                    unmatchedValue={transactionsData?.totalUnmatchedamount}
                    onClickMonthlyRecon={onClickMonthlyRecon}
                    onClickUnmatchedItems={onClickUnmatchedItems}
                />

                <div className='bg-gray-100 dark:bg-gray-800 p-4 rounded-md shadow-md my-10'>
                    <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4'>
                        <div>
                            <label className='block mb-1 font-medium dark:text-white'>Status (RAG)</label>
                            <SelectCheckbox
                                placeholder='All Statuses (Excluded Blue)'
                                options={STATUS_RAG}
                                selectedOption={selectedRag}
                                uniqueName='rag'
                                disabled={transactionsView === 'voided'}
                                onChangeSelectedOption={onChangeSelectedFilter}
                            />
                        </div>
                        <div>
                            <label className='block mb-1 font-medium dark:text-white'>Transaction view</label>
                            <div className={'my-2 rounded-full w-auto bg-white dark:bg-black flex flex-row items-center justify-between py-1 px-1'}>
                                <div
                                    onClick={() => onChangeTransactionsView('main-view')}
                                    className={`text-md px-3 cursor-pointer w-[55%] text-center hover:opacity-90 ${transactionsView === 'main-view' ? 'bg-[#252B37] dark:bg-bgColor-darkMode text-white dark:text-white rounded-full' : 'text-black dark:text-gray-600'} mr-2`}>
                                    <p>Main View</p>
                                </div>
                                <div
                                    onClick={() => onChangeTransactionsView('voided')}
                                    className={`text-md px-3 cursor-pointer w-[45%] text-center mb-2 md:mb-0 hover:opacity-90 ${transactionsView === 'voided' ? 'bg-[#252B37] dark:bg-bgColor-darkMode text-white dark:text-white rounded-full' : 'text-black dark:text-gray-600'}`}>
                                    <p>Voided</p>
                                </div>
                            </div>
                        </div>
                        <div>
                            <label className='block mb-1 font-medium dark:text-white'>Mismatch Reason</label>
                            <SelectCheckbox
                                placeholder='All Reasons'
                                options={reasonsData}
                                selectedOption={selectedMismatch}
                                uniqueName='mismatch'
                                onChangeSelectedOption={onChangeSelectedFilter}
                            />
                        </div>
                        <div>
                            <label className='block mb-1 font-medium dark:text-white'>Department</label>
                            <SelectCheckbox
                                placeholder='All Departments'
                                options={departmentsData}
                                selectedOption={selectedDepartment}
                                uniqueName='department'
                                onChangeSelectedOption={onChangeSelectedFilter}
                            />
                        </div>

                    </div>
                    <div className='grid grid-cols-1 md:grid-cols-1 lg:grid-cols-4 gap-4'>
                        <div>
                            <label className='block mb-1 font-medium dark:text-white'>Type</label>
                            <SelectCheckbox
                                placeholder='All Transactions'
                                options={typesData}
                                selectedOption={selectedTransactions}
                                uniqueName='transactions'
                                onChangeSelectedOption={onChangeSelectedFilter}
                            />
                        </div>
                        <div>
                            <label className='block mb-1 font-medium dark:text-white'>File Type</label>
                            <SelectCheckbox
                                placeholder='All Files'
                                options={filesTypeData}
                                uniqueName='fileType'
                                selectedOption={selectedFileType}
                                onChangeSelectedOption={onChangeSelectedFileType}
                            />
                        </div>
                        <RangeSlider
                            label='Variance Amount'
                            min={varianceAmountData?.min || 0}
                            max={varianceAmountData?.max || 0}
                            value={varianceAmount || varianceAmountData?.max || 0}
                            onChange={onChangeVarianceAmount}
                        />
                        <RangeSlider
                            label='Transaction Amount'
                            min={transactionAmountData?.min || 0}
                            max={transactionAmountData?.max || 0}
                            value={transactionAmount || transactionAmountData?.max || 0}
                            onChange={onChangeTransactionsAmount}
                        />
                    </div>

                    <div className='grid grid-cols-1 md:grid-cols-1 lg:grid-cols-6 gap-4 mt-4'>
                        <div className='lg:col-span-2'>
                            <DateRangePopover
                                label='Transaction Date Range - (POS)'
                                value={posDateRange}
                                showTime={true}
                                onChange={onChangeTransactionDatePOS}
                            />
                        </div>
                        <div className='lg:col-span-2'>
                            <DateRangePopover
                                label='Transaction Date Range - (PSP)'
                                value={pspDateRange}
                                showTime={true}
                                onChange={onChangeTransactionDatePSP}
                            />
                        </div>

                        <div className='pt-2'>
                            <Input
                                placeholder={'Search'}
                                containerStyle='mb-0 md:mt-5 relative dark:bg-bgColor-darkMode'
                                inputStyle={`mb-0 border-0 focus:outline-none dark:bg-black dark:text-primary-dark`}
                                icon={theme === 'dark' ? '/assets/navigation-icons/sidebar/search-icon-white.svg' : '/assets/shared/search.svg'}
                                showValue={true}
                                inputValue={searchText}
                                inputUniqueName={'search'}
                                onChangeInput={onChangeSearchValue}
                                hideInputSpace={true}
                            />
                        </div>
                        <div className='flex flex-row justify-center items-center'>
                            <Button
                                label={'Reset'}
                                icon={<img src={theme === 'dark' ? '/assets/insights-tab/reset-white.svg' : '/assets/insights-tab/reset.svg'} className='object-contain w-[24px] mr-2' />}
                                className='btn-primary-rounded !rounded-full !bg-white dark:!bg-black !text-[#1E4380] dark:!text-white !px-4 flex flex-row items-center justify-center mt-4'
                                onClickButton={resetFilters}
                            />
                        </div>
                    </div>
                </div>
                <div className='flex flex-col md:flex-row-reverse md:items-center md:justify-between'>
                    <div className='my-4 flex flex-col md:flex-row md:items-center'>
                        <Button
                            label={'Column Picker'}
                            scndIcon={<img src={'/assets/insights-tab/settings.svg'} className='object-contain w-[22px] ml-2' />}
                            className='btn-primary-rounded-less dark:!bg-black md:mr-3 flex flex-row items-center justify-center mb-3 bg-[#1E4380] dark:bg-black md:mb-0'
                            onClickButton={onClickColumnsModal}
                        />
                        <Button
                            label={'Export'}
                            scndIcon={<img src={'/assets/insights-tab/download-icon.svg'} className='object-contain w-[22px] ml-2' />}
                            className='btn-primary-rounded-less dark:!bg-black flex flex-row items-center justify-center mb-3 md:mb-0 md:mr-3'
                            onClickButton={onClickExportModal}
                        />
                        <Button
                            label={loadingExportReport ? 'Preparing report...' : 'Produce Output Report'}
                            className={`${!loadingExportReport ? 'btn-primary-rounded-less dark:!bg-black' : 'btn-primary-rounded-less-disable dark:!bg-[#3f3f3f] cursor-not-allowed'} flex flex-row items-center justify-center`}
                            onClickButton={() => !loadingExportReport && onClickExportReport()}
                        />
						<Button
                            label={'Add Manual transaction'}
                            className={`btn-primary-rounded-less dark:!bg-black flex flex-row items-center justify-center ml-2`}
                            onClickButton={handleShowManualTransactionModal}
                        />
                    </div>
                    <div className='flex flex-col md:flex-row md:items-center'>
                        <p className='dark:text-primary-dark md:mr-2'>Showing <span className='font-bold'>{itemsNumber} out of {paginationData?.totalElements}</span> Transactions</p>
						{/* {!allSelected && (
                            <div className='flex flex-row my-1 md:my-0 ml-0 md:ml-2'>
                                {selectedStatus?.map(status => (
                                    <div className='flex flex-row items-center my-1 md:my-0 bg-[#F5F5F5] dark:bg-black mr-2 px-2 py-1 rounded-md' key={status}>
                                        <div
                                            onClick={() => handleStatusToggle(status)}
                                            className='cursor-pointer hover:opacity-80'>
                                            <img src={theme === 'dark' ? '/assets/shared/close-icon-white.svg' : '/assets/shared/close-icon.svg'} className='object-contain w-[20px] mr-2' />
                                        </div>
                                        <p className='dark:text-primary-dark'>{status}</p>
                                    </div>
                                ))}
                            </div>
                        )} */}
                    </div>
                </div>
                <div>
                    {transactionsView === 'main-view' ?
                        <TransactionsTable
                            rows={rows}
                            paginationData={paginationData}
                            paginationState={paginationState}
                            selectedStatuses={selectedStatuses}
                            sortSelected={sortSelected}
                            onOpenTransactionDetails={onOpenTransactionDetails}
                            onClickShowTransactionModal={onClickShowTransactionModal}
                            handleSortChange={handleSortChange}
                            handleStatusToggle={handleStatusToggle}
                            setPaginationState={setPaginationState}
                            columns={allColumns && allColumns?.length > 0 ? allColumns?.filter((item: any) => !item?.hide) || [] : []}
                        /> :
                        <VoidedTable
                            rows={rows}
                            paginationData={paginationData}
                            paginationState={paginationState}
                            sortSelected={sortSelected}
                            onClickShowTransactionModal={onClickShowTransactionModal}
                            onOpenTransactionDetails={onOpenTransactionDetails}
                            handleSortChange={handleSortChange}
                            setPaginationState={setPaginationState}
                            columns={allColumns && allColumns?.length > 0 ? allColumns?.filter((item: any) => !item?.hide) || [] : []}
                        />
                    }
                </div>
                {showColumnsModal &&
                    <ColumnPickerModal
                        openModal={showColumnsModal}
                        handleCloseModal={onClickColumnsModal}
                        columns={allColumns || []}
                        onChangeActiveColumns={onChangeActiveColumns}
                    />
                }
                {showExportModal &&
                    <ExportModal
                        openModal={showExportModal}
                        handleCloseModal={onClickExportModal}
                        data={rows}
                        onGettingFilters={onGettingFilters}
                        extraData={transactionsData}
                        columns={allColumns && allColumns?.length > 0 ? allColumns?.filter((item: any) => !item?.hide) || [] : []}
                    />
                }
                {showDetailsModal?.show &&
                    <ReviewTransactionsModal
                        openModal={showDetailsModal?.show}
                        mainTransaction={showDetailsModal?.row}
                        columns={allColumns && allColumns?.length > 0 ? allColumns?.filter((item: any) => !item?.hide) || [] : []}
                        transactions={showDetailsModal?.row?.status === 'voided transaction' ? showDetailsModal?.row?.voidedMatches : showDetailsModal?.row?.multipleReferenceMatches}
                        status={showDetailsModal?.row?.status || ''}
                        handleCloseModal={() => setShowDetailsModal(undefined)}
                        onReviewSuccessfully={onReviewSuccessfully}
                    />
                }
                {showTransactionModal?.show &&
                    <TransactionsDetailsModal
                        openModal={showTransactionModal?.show}
                        transactionDetails={showTransactionModal?.row}
                        onOpenReview={onOpenTransactionDetails}
                        handleCloseModal={() => setShowTransactionModal(undefined)}
                    />
                }
                {unmatchedModal &&
                    <UnmatchedTransactionsModal
                        openModal={unmatchedModal}
                        typesData={typesData}
                        columns={allColumns && allColumns?.length > 0 ? allColumns?.filter((item: any) => !item?.hide) || [] : []}
                        departmentsData={departmentsData}
                        filesTypeData={filesTypeData}
                        reasonsData={reasonsData}
                        selectedLatestRun={selectedLatestRun}
                        selectedDateRange={selectedDateRange}
                        varianceAmountData={varianceAmountData}
                        handleCloseModal={onClickUnmatchedItems}
                    />
                }
                {showBankModal &&
                    <BankReconModal
                        open={showBankModal}
                        onClose={onClickMonthlyRecon}
                        monthSelected={selectedDateRange?.[1] ? moment(selectedDateRange?.[1]).toDate() : specificDate ? moment(specificDate?.[1]).toDate() : moment(new Date).toDate()}
                    />
                }
            </div>
        </div>
    );
};

export default DetailedTab;
